import React, {useState} from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import AccountCard from './AccountCard';
import {controlClass, fieldError, primaryButtonClass, secondaryButtonClass, Spinner} from '../formStyles';
import {CheckIcon} from '../Icons';

interface EmailCardProps {
    /** The address Supabase auth holds — the one sign-in and password reset actually use. */
    currentEmail: string;
    /** A requested address still waiting on its confirmation link, if any. */
    pendingEmail: string;
    error: string | null;
    submitting: boolean;
    /** Set once a confirmation has just been sent, so the card can say where it went. */
    sentTo: string | null;
    onSubmit: (email: string) => Promise<void>;
}

const EmailCard: React.FC<EmailCardProps> = ({
  currentEmail,
  pendingEmail,
  error,
  submitting,
  sentTo,
  onSubmit,
}) => {
  const [editing, setEditing] = useState(false);

  const emailSchema = Yup.object({
    email: Yup.string()
      .email('Please enter a valid email address')
      .notOneOf([currentEmail.toLowerCase()], 'That is already your email address')
      .required('Email is required'),
  });

  return (
    <AccountCard
      title="Email address"
      description="Used to sign in and to reset your password."
      error={error}
    >
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-gray-200">{currentEmail || '—'}</span>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} className={secondaryButtonClass}>
                        Change email
          </button>
        )}
      </div>

      {sentTo && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-500/30
         bg-emerald-500/[0.06] px-3 py-2.5 text-sm text-emerald-200">
          <CheckIcon className="mt-0.5 w-4 h-4 shrink-0" />
          <span>
                        Confirmation sent to <span className="font-medium">{sentTo}</span>. Click the link in that
                        email to finish the change — until you do, keep signing in with your current address.
          </span>
        </div>
      )}

      {/*
        A change that was requested but never confirmed. Worth stating outright: the user asked to
        move address, nothing happened, and they have no reason to know the link is still sitting in
        an inbox somewhere.
      */}
      {!sentTo && pendingEmail && (
        <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5
         text-sm text-amber-200">
                    A change to <span className="font-medium">{pendingEmail}</span> is waiting to be confirmed.
                    Use the link in that email, or request a new one below.
        </p>
      )}

      {editing && (
        <Formik
          initialValues={{email: ''}}
          validationSchema={emailSchema}
          onSubmit={async (values, {setSubmitting, resetForm}) => {
            await onSubmit(values.email.trim().toLowerCase());
            setSubmitting(false);
            resetForm();
          }}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({isSubmitting}) => {
            const busy = isSubmitting || submitting;

            return (
              <Form className="mt-4 space-y-3.5 border-t border-gray-800 pt-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="new-email">
                                        New email address
                  </label>
                  <Field
                    id="new-email"
                    name="email"
                    type="text"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@example.com"
                    className={controlClass}
                  />
                  <ErrorMessage name="email">{fieldError}</ErrorMessage>
                  <p className="mt-1 text-xs text-gray-600">
                                        We&apos;ll email a confirmation link there. The change only takes effect once
                                        you click it.
                  </p>
                </div>

                <div className="flex gap-2">
                  <button type="submit" disabled={busy} className={primaryButtonClass}>
                    {busy && <Spinner />}
                    {busy ? 'Sending…' : 'Send confirmation'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing(false)}
                    className={secondaryButtonClass}
                  >
                                        Cancel
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </AccountCard>
  );
};

export default EmailCard;
